import React, { useRef, useContext, useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { isBefore, parseISO } from 'date-fns';

import DatePicker from './DatePicker';
import { formatDate } from '@spa/shared/utils';
import { CloseIcon } from '@spa/shared/svg/close';
import Notifications from './notifications';
import { Header, WrapperMobile, WrapperDesktop, ButtonsMobile, ButtonsDesktop } from './styles';
import { MediaContext } from '@spa/shared/contexts/media';
import Button from '@spa/shared/components/Unisystem/Button';
import { CONTRACT_TYPE } from '@spa/shared/constants/contractType';
import PaymentSummary from '@spa/shared/components/PaymentSummary';
import { isRangeValid } from '@spa/shared/utils/availabilities/range-valid';
import { getAvailableFrom } from '@spa/shared/utils/transformers/contracts';
import FixedContracts from '@spa/shared/components/Unisystem/Calendar/FixedContracts';
import useOnOutsideClickOrPressEsc from '@spa/shared/hooks/useOnOutsideClickOrPressEsc';
import { trackAmplitude } from '@spa/shared/hooks/tracking/services/amplitude/dataAmplitude';
import events from '@spa/shared/hooks/tracking/events';
import ReserveWarning from '../../ReserveWarning';
import { CalendarProvider } from '@spa/shared/contexts/calendar';

const Calendar = ({
  onClose,
  accommodationOffer,
  property,
  priceDetailsRequest,
  priceDetailsData,
  setMoveInOut,
  setGuests,
  selectFixedOption,
  onConfirm,
  extraPrice,
  isLoading,
  classNames = {},
  setShowPaymentSummary = () => {},
  showPaymentSummary = false,
  source
}) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const { is } = useContext(MediaContext);
  const ref = useRef();
  useOnOutsideClickOrPressEsc(ref, onClose);

  const {
    contract: {
      type: contractType,
      is_instant_booking: isInstantBooking,
      fixed,
      fixed_unitary: fixedUnitary,
      standard: { rents }
    },
    availabilities: { standard, standard_unitary: standardUnitary, availability_rules: availabilityRules = [] },
    requisites: {
      conditions: { max_guests: maxGuests, minimum_nights: conditionsMinimumNights }
    },
    reference_price: referencePrice
  } = accommodationOffer;

  const availablePeriods = standardUnitary
    ? standardUnitary?.available_periods ?? []
    : standard?.available_periods ?? [];
  const blockedIntervals = standardUnitary ? standardUnitary?.blocked_intervals ?? [] : [];

  const minimumStay =
    Math.max(conditionsMinimumNights || 0, availabilityRules[0]?.gap?.move_in_out?.items?.[0]?.end?.min?.days || 0) ??
    null;
  const maximumStay = availabilityRules[0]?.gap?.move_in_out?.items?.[0]?.end?.max?.days ?? null;

  const { first_payment: firstPayment } = priceDetailsData;
  const { moveIn, moveOut, guests, optionId } = priceDetailsRequest;
  const isAnyTypeFixed = [CONTRACT_TYPE.FIXED, CONTRACT_TYPE.FIXED_UNITARY].includes(contractType);

  const availableFrom = getAvailableFrom(
    accommodationOffer,
    availabilityRules[0]?.gap?.move_in_out?.items?.[0]?.start?.min?.days
  );

  const [validations, setValidations] = useState(
    !isAnyTypeFixed && isRangeValid(moveIn, moveOut, t, language, accommodationOffer)
  );

  useEffect(() => {
    setValidations(!isAnyTypeFixed && isRangeValid(moveIn, moveOut, t, language, accommodationOffer));
  }, [moveIn, moveOut, showPaymentSummary]);

  const clearDates = useCallback(() => setMoveInOut(null, null), [isAnyTypeFixed, onClose]);

  const handleConfirm = useCallback(() => {
    if (!isAnyTypeFixed) {
      trackAmplitude(events.datesApplied, { moveIn, moveOut, source });
    }

    if (isAnyTypeFixed) {
      const selectedOption = (fixed?.options || fixedUnitary?.options).find(option => option.id === optionId);
      const { start_date: startDate, end_date: endDate } = selectedOption;

      trackAmplitude(events.datesApplied, { moveIn: startDate, moveOut: endDate, source });
    }

    onConfirm(moveIn, moveOut, guests, optionId);

    if (is.mobile) {
      setShowPaymentSummary(true);
    }
  }, [moveIn, moveOut, guests, optionId, onConfirm, fixed?.options, fixedUnitary?.options]);

  const validateDates = (startDate, endDate) => {
    const validation = !isAnyTypeFixed && isRangeValid(startDate, endDate, t, language, accommodationOffer);

    setValidations(validation);

    return !validation.error;
  };

  const classes = { ...classNames, 'has-notifications': !!validations.error };

  const [Wrapper, Buttons] = useMemo(
    () => (is.mobile ? [WrapperMobile, ButtonsMobile] : [WrapperDesktop, ButtonsDesktop]),
    [is.mobile]
  );

  return (
    <CalendarProvider validateDates={validateDates}>
      <Wrapper ref={ref} isBig={!isAnyTypeFixed} showPaymentSummary={showPaymentSummary}>
        {showPaymentSummary ? (
          <React.Fragment>
            <Header>
              <CloseIcon onClick={onClose} />
              <Button isTertiary onClick={() => setShowPaymentSummary(false)}>
                {t('Edit dates')}
              </Button>
            </Header>
            <PaymentSummary
              accommodationOffer={accommodationOffer}
              property={property}
              priceDetails={priceDetailsData}
              onClose={onClose}
              onEditDates={() => setShowPaymentSummary(false)}
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            {is.mobile && (
              <Header hasSingleChild={!showPaymentSummary}>
                <CloseIcon onClick={onClose} />
              </Header>
            )}
            {isAnyTypeFixed ? (
              <FixedContracts
                guests={guests}
                maxGuests={maxGuests}
                setGuests={setGuests}
                options={fixed?.options || fixedUnitary?.options}
                selectedOption={optionId}
                selectOption={selectFixedOption}
                classNames={classNames}
              />
            ) : (
              <DatePicker
                guests={guests}
                maxGuests={maxGuests}
                setGuests={setGuests}
                firstPayment={firstPayment?.total}
                referencePrice={referencePrice}
                minimumStay={minimumStay}
                maximumStay={maximumStay}
                availableFrom={availableFrom}
                moveIn={moveIn ? formatDate(parseISO(moveIn)) : null}
                moveOut={moveOut ? formatDate(parseISO(moveOut)) : null}
                setMoveInOut={setMoveInOut}
                classNames={classes}
                rents={rents}
                availablePeriods={availablePeriods}
                blockedDates={blockedIntervals.filter(
                  ({ end_date: date }) =>
                    isBefore(new Date(), new Date(date)) && isBefore(availableFrom, new Date(date))
                )}
                availabilityRules={availabilityRules}
              />
            )}
          </React.Fragment>
        )}
        {!showPaymentSummary && (
          <Notifications
            validations={validations}
            isMobile={is.mobile}
            extraData={{ extraPrice, isInstantBooking, setMoveInOut }}
          />
        )}
        <Buttons showPaymentSummary={showPaymentSummary}>
          {!showPaymentSummary && (
            <Button isGhost onClick={isAnyTypeFixed ? onClose : clearDates} isDisabled={isLoading}>
              {t(isAnyTypeFixed ? 'Cancel' : 'Clear Dates')}
            </Button>
          )}
          <Button
            isPrimary
            onClick={handleConfirm}
            isDisabled={((!moveIn || !moveOut || !validations.isValid) && !optionId) || isLoading}
          >
            {t(showPaymentSummary ? 'Request to book' : isAnyTypeFixed ? 'Confirm Dates' : 'Select dates')}
          </Button>
          {!!showPaymentSummary && (
            <ReserveWarning
              // eslint-disable-next-line quotes
              message={t("Don't worry - pressing this button won't charge you anything!")}
            />
          )}
        </Buttons>
      </Wrapper>
    </CalendarProvider>
  );
};

Calendar.propTypes = {
  onClose: PropTypes.func,
  accommodationOffer: PropTypes.object,
  property: PropTypes.object,
  priceDetailsRequest: PropTypes.object,
  priceDetailsData: PropTypes.object,
  setMoveInOut: PropTypes.func,
  setGuests: PropTypes.func,
  selectFixedOption: PropTypes.func,
  onConfirm: PropTypes.func,
  extraPrice: PropTypes.object,
  isLoading: PropTypes.bool,
  classNames: PropTypes.object,
  showPaymentSummary: PropTypes.bool,
  setShowPaymentSummary: PropTypes.func,
  source: PropTypes.string
};

export default Calendar;
